'use client';

/**
 * ca_preview — the owner-approval gate for the store-integration redesign.
 *
 * Everything new from the AAA store redesign (store header/footer, add to
 * cart from gear recommendations, the cart confirmation) renders ONLY when
 * this flag is on, and only for the camping-australia brand. The owner
 * approved the redesign, so it is ON by default for camping-australia.
 * EscapeTo never gets it.
 *
 * Opting out (so the owner can still compare against the legacy UI):
 *   - `?ca_preview=0` on any planner URL turns it off and persists the
 *     opt-out in a `ca_preview=0` cookie.
 *   - `?ca_preview=1` turns it back on and overwrites the cookie with
 *     `ca_preview=1` (the AAA theme sets the same value; harmless).
 *   - With no query param, only a `ca_preview=0` cookie turns it off.
 *
 * The inline boot script in app/layout.tsx mirrors this decision; keep the
 * two in step.
 *
 * The flag is read after mount (never during SSR) so the server HTML and the
 * first client render are identical — no hydration mismatch, no cache split.
 */

import { useEffect, useState } from 'react';
import { BRAND } from './brand';

export const CA_PREVIEW_COOKIE = 'ca_preview';
const MAX_AGE = 60 * 60 * 24 * 30;

/** Pure decision, exported for tests. */
export function resolveCaPreview(
  brandKey: string,
  search: string,
  cookie: string,
): { on: boolean; write: 'set' | 'optout' | null } {
  if (brandKey !== 'camping-australia') return { on: false, write: null };
  const q = new URLSearchParams(search).get(CA_PREVIEW_COOKIE);
  if (q === '1') return { on: true, write: 'set' };
  if (q === '0') return { on: false, write: 'optout' };
  const optedOut = cookie.split(';').some((c) => c.trim() === `${CA_PREVIEW_COOKIE}=0`);
  return { on: !optedOut, write: null };
}

let cached: boolean | null = null;

export function readCaPreview(): boolean {
  if (cached !== null) return cached;
  if (typeof window === 'undefined') return false;
  let on = false;
  try {
    const r = resolveCaPreview(BRAND.key, window.location.search, document.cookie);
    on = r.on;
    if (r.write === 'set') {
      document.cookie = `${CA_PREVIEW_COOKIE}=1; path=/; max-age=${MAX_AGE}; SameSite=Lax`;
    } else if (r.write === 'optout') {
      document.cookie = `${CA_PREVIEW_COOKIE}=0; path=/; max-age=${MAX_AGE}; SameSite=Lax`;
    }
  } catch {
    on = false;
  }
  cached = on;
  document.documentElement.classList.toggle('ca-preview', on);
  return on;
}

/** False on the server and on the first client render; true after mount when the flag is on. */
export function useCaPreview(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    setOn(readCaPreview());
  }, []);
  return on;
}
